import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { meQuery, projectQuery, projectsQuery } from "./api/queries";
import { ApiError } from "./api/http";
import { RootLayout, NotFound } from "./routes/root";
import { SignInPage, SignUpPage } from "./routes/auth";
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "./routes/recovery";
import { OnboardingLayout, CreateOrgPage, CreateProjectPage } from "./routes/onboarding";
import { ProjectLayout, ProjectError } from "./routes/project-layout";
import { ComingSoonPage } from "./routes/coming-soon";
import { InstallPage } from "./routes/install";
import { InsightsLayout, InsightSoon } from "./routes/insights/layout";
import { SettingsLayout } from "./routes/settings/layout";
import { LAST_PROJECT_KEY } from "./lib/workspace";
import { INSIGHT_KINDS } from "./api/schemas";
import { prefs } from "./lib/storage";
import type { SectionKey } from "./nav";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});

async function requireMe(queryClient: QueryClient, href: string) {
  const me = await queryClient.ensureQueryData(meQuery);
  if (!me) throw redirect({ to: "/sign-in", search: { redirect: href } });
  return me;
}

/** Sends a signed-in user to their last project, or to the next onboarding step. */
async function landingFor(queryClient: QueryClient): Promise<never> {
  const me = await queryClient.ensureQueryData(meQuery);
  if (!me) throw redirect({ to: "/sign-in", search: {} });
  const firstOrg = me.orgs[0];
  if (!firstOrg) throw redirect({ to: "/onboarding/org" });
  const last = prefs.get(LAST_PROJECT_KEY);
  if (last) {
    try {
      await queryClient.ensureQueryData(projectQuery(last));
      throw redirect({ to: "/p/$projectId", params: { projectId: last } });
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  }
  for (const org of me.orgs) {
    const projects = await queryClient.ensureQueryData(projectsQuery(org.id));
    const p = projects[0];
    if (p) throw redirect({ to: "/p/$projectId", params: { projectId: p.id } });
  }
  throw redirect({ to: "/onboarding/project", search: { org: firstOrg.id } });
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: ({ context }) => landingFor(context.queryClient),
});

const authSearch = z.object({ redirect: z.string().optional() });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: authSearch,
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(meQuery)) await landingFor(context.queryClient);
  },
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(meQuery)) await landingFor(context.queryClient);
  },
  component: SignUpPage,
});

// The API emails links to exactly these paths (crates/api), so they must not move.
const forgotRoute = createRoute({ getParentRoute: () => rootRoute, path: "/forgot-password", component: ForgotPasswordPage });
const tokenSearch = z.object({ token: z.string().optional() });
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: tokenSearch,
  component: ResetPasswordPage,
});
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  validateSearch: tokenSearch,
  component: VerifyEmailPage,
});

/**
 * `/settings/connections` (and `/settings/account`, `/settings/tokens`) are personal, not per
 * project, and the API links to them from emails: open them inside the last project the user used.
 */
const personalSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/$section",
  beforeLoad: async ({ context, location, params }) => {
    await requireMe(context.queryClient, location.href);
    const section = ["account", "connections", "tokens", "billing"].includes(params.section) ? params.section : "account";
    const projectId = await someProjectId(context.queryClient);
    if (!projectId) return landingFor(context.queryClient);
    throw redirect({ to: `/p/$projectId/settings/${section}` as "/p/$projectId/settings/account", params: { projectId } });
  },
});

/** The last project the user opened if they can still see it, else their first one. */
async function someProjectId(queryClient: QueryClient): Promise<string | null> {
  const me = await queryClient.ensureQueryData(meQuery);
  if (!me) return null;
  const last = prefs.get(LAST_PROJECT_KEY);
  if (last) {
    try {
      await queryClient.ensureQueryData(projectQuery(last));
      return last;
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  }
  for (const org of me.orgs) {
    const first = (await queryClient.ensureQueryData(projectsQuery(org.id)))[0];
    if (first) return first.id;
  }
  return null;
}

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: async ({ context, location }) => ({ me: await requireMe(context.queryClient, location.href) }),
  component: OnboardingLayout,
});

const onboardingOrgRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/org",
  component: CreateOrgPage,
});

const onboardingProjectRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/project",
  validateSearch: z.object({ org: z.string().optional() }),
  component: CreateProjectPage,
});

export const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/p/$projectId",
  beforeLoad: async ({ context, location }) => ({ me: await requireMe(context.queryClient, location.href) }),
  loader: async ({ context, params }) => {
    const project = await context.queryClient.ensureQueryData(projectQuery(params.projectId));
    prefs.set(LAST_PROJECT_KEY, project.id);
    return { project };
  },
  component: ProjectLayout,
  errorComponent: ProjectError,
});

const homeRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./routes/home"), "HomePage"),
});
const installRoute = createRoute({ getParentRoute: () => projectRoute, path: "/install", component: InstallPage });

const insightsRoute = createRoute({ getParentRoute: () => projectRoute, path: "/insights", component: InsightsLayout });
const insightsIndexRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/p/$projectId/insights/trends", params });
  },
});
/** `?insight=<id>`: the builder was opened from a saved insight. */
const openedInsightSearch = z.object({ insight: z.string().optional() });
const savedInsightsRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/saved",
  validateSearch: z.object({
    q: z.string().optional(),
    kind: z.enum(INSIGHT_KINDS).optional(),
    tag: z.string().optional(),
    fav: z.boolean().optional(),
  }),
  component: lazyRouteComponent(() => import("./routes/insights/saved"), "SavedInsightsPage"),
});
const trendsRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/trends",
  validateSearch: openedInsightSearch,
  component: lazyRouteComponent(() => import("./routes/insights/trends"), "TrendsPage"),
});
const funnelsRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/funnels",
  validateSearch: openedInsightSearch,
  component: lazyRouteComponent(() => import("./routes/insights/funnels"), "FunnelsPage"),
});
const retentionRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/retention",
  validateSearch: openedInsightSearch,
  component: lazyRouteComponent(() => import("./routes/insights/retention"), "RetentionPage"),
});
const pathsRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/paths",
  validateSearch: openedInsightSearch,
  component: lazyRouteComponent(() => import("./routes/insights/paths"), "PathsPage"),
});
const sqlRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/sql",
  // CodeMirror only loads with this route.
  component: lazyRouteComponent(() => import("./routes/insights/sql"), "SqlPage"),
});
const insightSoonRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/$kind",
  component: InsightSoon,
});

const settingsRoute = createRoute({ getParentRoute: () => projectRoute, path: "/settings", component: SettingsLayout });
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/p/$projectId/settings/project", params });
  },
});
const settingsProjectRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/project",
  component: lazyRouteComponent(() => import("./routes/settings/project"), "ProjectSettingsPage"),
});
const settingsTokensRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/tokens",
  component: lazyRouteComponent(() => import("./routes/settings/tokens"), "TokensPage"),
});
const settingsConnectionsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/connections",
  component: lazyRouteComponent(() => import("./routes/settings/connections"), "ConnectionsPage"),
});
const settingsBillingRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/billing",
  component: lazyRouteComponent(() => import("./routes/settings/billing"), "BillingPage"),
});
const settingsAccountRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/account",
  component: lazyRouteComponent(() => import("./routes/settings/account"), "AccountPage"),
});

const dashboardsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/dashboards",
  component: lazyRouteComponent(() => import("./routes/dashboards/list"), "DashboardsPage"),
});
const dashboardRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/dashboards/$dashboardId",
  component: lazyRouteComponent(() => import("./routes/dashboards/dashboard"), "DashboardPage"),
});

const cohortsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/cohorts",
  component: lazyRouteComponent(() => import("./routes/cohorts/list"), "CohortsPage"),
});
const newCohortRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/cohorts/new",
  validateSearch: z.object({ kind: z.enum(["dynamic", "static"]).optional() }),
  component: lazyRouteComponent(() => import("./routes/cohorts/cohort"), "NewCohortPage"),
});
const cohortRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/cohorts/$cohortId",
  component: lazyRouteComponent(() => import("./routes/cohorts/cohort"), "CohortPage"),
});

const catalogRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/catalog",
  component: lazyRouteComponent(() => import("./routes/catalog/catalog"), "CatalogLayout"),
});
const catalogIndexRoute = createRoute({
  getParentRoute: () => catalogRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/p/$projectId/catalog/events", params });
  },
});
const catalogEventsRoute = createRoute({
  getParentRoute: () => catalogRoute,
  path: "/events",
  component: lazyRouteComponent(() => import("./routes/catalog/catalog"), "CatalogEventsPage"),
});
const catalogPropertiesRoute = createRoute({
  getParentRoute: () => catalogRoute,
  path: "/properties",
  component: lazyRouteComponent(() => import("./routes/catalog/catalog"), "CatalogPropertiesPage"),
});
const catalogLiveRoute = createRoute({
  getParentRoute: () => catalogRoute,
  path: "/live",
  component: lazyRouteComponent(() => import("./routes/catalog/live"), "LiveEventsPage"),
});

const peopleRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/people",
  component: lazyRouteComponent(() => import("./routes/people/people"), "PeopleLayout"),
});
const peopleIndexRoute = createRoute({
  getParentRoute: () => peopleRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/p/$projectId/people/persons", params });
  },
});
const personsRoute = createRoute({
  getParentRoute: () => peopleRoute,
  path: "/persons",
  component: lazyRouteComponent(() => import("./routes/people/people"), "PersonsPage"),
});
const personRoute = createRoute({
  getParentRoute: () => peopleRoute,
  path: "/persons/$personId",
  component: lazyRouteComponent(() => import("./routes/people/person"), "PersonPage"),
});
const groupsRoute = createRoute({
  getParentRoute: () => peopleRoute,
  path: "/groups",
  component: lazyRouteComponent(() => import("./routes/people/people"), "GroupsPage"),
});

const soon = <K extends SectionKey>(key: K) =>
  createRoute({
    getParentRoute: () => projectRoute,
    path: `/${key}` as `/${K}`,
    component: () => <ComingSoonPage section={key} />,
  });
const soonRoutes = [
  soon("ask"),
  soon("replays"),
  soon("revenue"),
  soon("flags"),
  soon("experiments"),
  soon("findings"),
] as const;

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  signUpRoute,
  forgotRoute,
  resetRoute,
  verifyRoute,
  personalSettingsRoute,
  onboardingRoute.addChildren([onboardingOrgRoute, onboardingProjectRoute]),
  projectRoute.addChildren([
    homeRoute,
    installRoute,
    insightsRoute.addChildren([insightsIndexRoute, savedInsightsRoute, trendsRoute, funnelsRoute, retentionRoute, pathsRoute, sqlRoute, insightSoonRoute]),
    settingsRoute.addChildren([
      settingsIndexRoute,
      settingsProjectRoute,
      settingsTokensRoute,
      settingsAccountRoute,
      settingsConnectionsRoute,
      settingsBillingRoute,
    ]),
    dashboardsRoute,
    dashboardRoute,
    cohortsRoute,
    newCohortRoute,
    cohortRoute,
    peopleRoute.addChildren([peopleIndexRoute, personsRoute, personRoute, groupsRoute]),
    catalogRoute.addChildren([catalogIndexRoute, catalogEventsRoute, catalogPropertiesRoute, catalogLiveRoute]),
    ...soonRoutes,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
